import { useQueryClient } from '@tanstack/react-query';
import { alerta } from '../shared/alertaStore';
import { EVENT_TYPES, useRealtimeEvent } from '../../realtime';
import {
  ESTADO_QUERY_KEY,
  invalidateNotificacoes,
  normalizarEstado,
} from '../../hooks/useNotificacoes';

const PRIORITY_ALERTA = {
  critica: alerta.error,
  alta: alerta.warning,
  baixa: alerta.info,
  normal: alerta.info,
};

/**
 * Ponte única entre o socket e a UI:
 * - mostra toast quando chega `notificacao.nova`;
 * - invalida lista/contador em novos eventos e em leituras (inclusive outra aba);
 * - espelha o estado da área (`notificacao.estado`) para que todos os
 *   dispositivos abram a mesma janela.
 *
 * Deve ser montada uma única vez, dentro do `RealtimeProvider`.
 */
const NotificationToastBridge = () => {
  const queryClient = useQueryClient();

  useRealtimeEvent(EVENT_TYPES.NOTIFICACAO_NOVA, (data) => {
    const notificacao = data?.notificacao;

    if (notificacao?.titulo) {
      const notify = PRIORITY_ALERTA[notificacao.prioridade] || alerta.info;
      const message = notificacao.mensagem
        ? `${notificacao.titulo} — ${notificacao.mensagem}`
        : notificacao.titulo;

      notify(message);
    }

    invalidateNotificacoes(queryClient);
  });

  useRealtimeEvent(EVENT_TYPES.NOTIFICACAO_LIDA, () => {
    invalidateNotificacoes(queryClient);
  });

  useRealtimeEvent(EVENT_TYPES.NOTIFICACAO_REMOVIDA, () => {
    invalidateNotificacoes(queryClient);
  });

  // Outra aba/dispositivo trocou de aba ou categoria: aplica no cache antes de
  // revalidar, para a UI não piscar na janela antiga.
  useRealtimeEvent(EVENT_TYPES.NOTIFICACAO_ESTADO, (data) => {
    if (!data) return;

    queryClient.setQueryData(ESTADO_QUERY_KEY, normalizarEstado(data));
    invalidateNotificacoes(queryClient);
  });

  return null;
};

export default NotificationToastBridge;
